import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ViewWrapper } from "../components/ViewWrapper";

type Feature = { title: string; desc: string };
type Tier = {
  name: string;
  price: string;
  period?: string;
  tagline?: string;
  carry?: string;
  features: Feature[];
  badge?: string;
};

// ------------------------------- Data -------------------------------------

const FORMATION_TIERS: Tier[] = [
  {
    name: "Starter",
    price: "$99.00",
    tagline: "Perfect for basic startup companies with limited needs",
    features: [
      {
        title: "Anonymous & Private Filings (Registered Agent)",
        desc: "Keeps your personal name and contact details off public records for privacy and asset protection.",
      },
      {
        title: "Standard Operating Agreement",
        desc: "Defines how your business is run, who's in charge, and how decisions are made — essential for avoiding disputes and proving legitimacy.",
      },
      {
        title: "Prepare & File Articles of Organization (LLC)",
        desc: "Legally forms your business with the state so you can operate officially and open a bank account.",
      },
      {
        title: "Access to an Attorney",
        desc: "Gives you expert legal help to avoid mistakes, protect your interests, and stay compliant.",
      },
      {
        title: "Resolution to Open a Bank Account",
        desc: "Shows banks your company officially agreed to open a business account — often required to get started.",
      },
      {
        title: "Organizational Minutes",
        desc: "Documents your business's first major decisions — helps show you're properly set up and organized.",
      },
      {
        title: "Expedited 24hr Filing",
        desc: "Get your business formed or updated fast — perfect if you're on a tight deadline or need documents quickly.",
      },
    ],
  },
  {
    name: "Professional",
    price: "$199.00",
    tagline:
      "Perfect for running your business with increased customization and compliance",
    carry: "Everything in the Starter plan, plus:",
    badge: "Recommended for US",
    features: [
      {
        title: "24 hour Employer Identification Number (EIN)",
        desc: "Needed to open a business bank account, hire employees, and file taxes — like a Social Security number for your business.",
      },
      {
        title: "Instant Bank Account",
        desc: "Get a business bank account quickly without extra paperwork or long delays — perfect for getting started fast.",
      },
      {
        title: "Change Tax Election (including S-corp)",
        desc: "Choose how your business is taxed — can help you save money or better match your goals.",
      },
      {
        title: "Custom Operating Agreement & Unlimited Updates",
        desc: "Have a tailored agreement that fits your business — and update it anytime as things change.",
      },
      {
        title: "Free Amendment or Name Change in First 90 Days",
        desc: "Lets you make changes to your business name or structure early on — helpful if you change your mind or need to adjust quickly.",
      },
      {
        title: "Certificate of Good Standing",
        desc: "Proves your business is active and in compliance — often required for loans, contracts, or registering in other states.",
      },
    ],
  },
  {
    name: "International +",
    price: "$399.00",
    tagline: "Perfect for unique needs of international clients",
    carry: "Everything in the Professional plan, plus:",
    badge: "Recommended for Foreign",
    features: [
      {
        title: "Foreign Employer Identification Number (FEIN)",
        desc: "Get a U.S. tax ID for your foreign-owned company — required to open a bank account or do business in the U.S.",
      },
      {
        title: "Apostille",
        desc: "Makes your business documents valid internationally — often needed if you're doing business or opening accounts in other countries.",
      },
      {
        title: "Custom Certificate of Incumbency",
        desc: "Proves who legally represents your company — many banks, governments, or counterparties require proof of legal authority.",
      },
      {
        title: "One Foreign Registration Filing or One Subsidiary",
        desc: "Register your business in another state or create a subsidiary — needed if you're expanding or operating in multiple states.",
      },
      {
        title: "One Free DBA",
        desc: "Add an alternate business name to use for branding — helpful if you want to operate under a different name without forming a new company.",
      },
    ],
  },
];

const COMPLIANCE_TIERS: Tier[] = [
  {
    name: "Standard",
    price: "$14.99",
    period: "/mo",
    tagline:
      "Essential protection and compliance tools to keep your business in good standing.",
    features: [
      {
        title: "Registered Agent",
        desc: "Legally required in every state. We act as your official point of contact — shielding your personal details from public view and ensuring you never miss a notice.",
      },
      {
        title: "Annual Report Filing & Compliance",
        desc: "Ensures your business stays active and compliant with state requirements — avoids fines or dissolution.",
      },
      {
        title: "Physical Business Address",
        desc: "Gives your company a real street address — essential for credibility, banking, and receiving official mail.",
      },
      {
        title: "Essential Doc Templates including Annual Meeting Minutes",
        desc: "Records key decisions and shows your business is following proper procedures — important for legal and tax protection.",
      },
      {
        title: "5 Free Mail Scans Annually (Excluding Junk)",
        desc: "Get important business mail scanned and sent to you digitally — helps you stay informed, no matter where you are.",
      },
    ],
  },
  {
    name: "Professional",
    price: "$29.99",
    period: "/mo",
    tagline:
      "Advanced legal coverage and flexibility to support growing and evolving businesses.",
    carry: "Everything in the Standard plan, plus:",
    badge: "Recommended",
    features: [
      {
        title: "Unlimited Operating Agreement Changes",
        desc: "Make updates to your LLC's rules and structure anytime — helps you stay flexible as your business grows or changes.",
      },
      {
        title: "Unlimited Mail Scans",
        desc: "Get all your important mail scanned and emailed to you — no limit, no delays, and no missed documents.",
      },
      {
        title: "Auto-generated Board Resolutions",
        desc: "Provides quick, compliant documentation of key decisions — saving time, reducing legal risk, and ensuring corporate governance is properly maintained.",
      },
      {
        title: "Quarterly Meeting Minutes",
        desc: "Keep a clear record of key business decisions every quarter — helps with organization, compliance, and tax prep.",
      },
      {
        title: "Independent Contractor Agreement",
        desc: "Protects your business when hiring freelancers or contractors — clearly defines roles, responsibilities, and expectations.",
      },
      {
        title: "Non-Disclosure Agreement",
        desc: "Keeps your business information private — essential when sharing ideas or working with partners.",
      },
    ],
  },
  {
    name: "Virtual Office +",
    price: "$49.99",
    period: "/mo",
    tagline: "Guaranteed attorney consult if sued.",
    carry: "Everything in the Professional plan, plus:",
    features: [
      {
        title: "Virtual Office + Unique Address Lease",
        desc: "Run your business from anywhere with a real street address, business lease, and phone number — no office required. Adds credibility and keeps your personal info private.",
      },
      {
        title: "Free Attorney Consultation if Sued",
        desc: "Get expert legal advice if your business is sued — know your options and protect your rights without upfront costs.",
      },
      {
        title: "Unlimited Filings w/ State (No Handling, Pay ONLY State Fee)",
        desc: "Make unlimited changes like address updates or member changes — only pay the state, no extra service fees.",
      },
      {
        title: "Unlimited Certificate of Good Standing",
        desc: "Request as many official proof-of-compliance documents as needed — helpful for banks, investors, and contracts.",
      },
      {
        title: "Special Meeting Minutes",
        desc: "Record important one-time decisions — keeps your business organized and legally protected.",
      },
    ],
  },
];

// ------------------------------ Component ---------------------------------

export function PricingView() {
  return (
    <ViewWrapper>
      <div style={{ padding: "0 32px 40px", maxWidth: 1280, margin: "0 auto" }}>
        <Section title="Formation Tiers" tiers={FORMATION_TIERS} />
        <div style={{ height: 28 }} />
        <Section title="Ongoing Compliance" tiers={COMPLIANCE_TIERS} />
      </div>
    </ViewWrapper>
  );
}

function Section({ title, tiers }: { title: string; tiers: Tier[] }) {
  return (
    <section>
      <h2
        style={{
          margin: "0 0 16px",
          fontSize: 22,
          fontWeight: 700,
          color: "#fff",
        }}
      >
        {title}
      </h2>
      <div className="pricing-grid">
        {tiers.map((t) => (
          <TierCard key={t.name} tier={t} />
        ))}
      </div>
    </section>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  const highlighted = !!tier.badge;
  return (
    <div
      style={{
        position: "relative",
        borderRadius: 18,
        padding: "22px 20px 22px",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        background: highlighted
          ? "rgba(99,102,241,0.20)"
          : "rgba(8,10,18,0.6)",
        border: highlighted
          ? "1px solid rgba(99,102,241,0.55)"
          : "1px solid rgba(255,255,255,0.10)",
        boxShadow: highlighted
          ? "0 8px 32px rgba(99,102,241,0.22)"
          : "0 8px 32px rgba(0,0,0,0.35)",
        color: "#fff",
      }}
    >
      {tier.badge && (
        <span
          style={{
            position: "absolute",
            top: -10,
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(90deg,#6366f1,#818cf8)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            padding: "4px 12px",
            borderRadius: 999,
            whiteSpace: "nowrap",
          }}
        >
          {tier.badge}
        </span>
      )}

      <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.85 }}>
        {tier.name}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", marginTop: 4 }}>
        <span style={{ fontSize: 28, fontWeight: 800 }}>{tier.price}</span>
        {tier.period && (
          <span style={{ marginLeft: 4, fontSize: 13, opacity: 0.7 }}>
            {tier.period}
          </span>
        )}
      </div>

      {tier.tagline && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            opacity: 0.78,
            lineHeight: 1.45,
          }}
        >
          {tier.tagline}
        </div>
      )}

      {tier.carry && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            opacity: 0.7,
            fontStyle: "italic",
          }}
        >
          {tier.carry}
        </div>
      )}

      <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0" }}>
        {tier.features.map((f) => (
          <FeatureRow key={f.title} feature={f} />
        ))}
      </ul>
    </div>
  );
}

function FeatureRow({ feature }: { feature: Feature }) {
  const rowRef = useRef<HTMLLIElement | null>(null);
  const [tip, setTip] = useState<{ left: number; top: number } | null>(null);

  function openTip() {
    const el = rowRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const TIP_W = 280;
    let left = r.right + 12;
    // Flip to the left if there's no room on the right.
    if (left + TIP_W + 12 > window.innerWidth) {
      left = r.left - TIP_W - 12;
    }
    if (left < 8) left = 8;
    const top = Math.max(8, r.top + r.height / 2 - 40);
    setTip({ left, top });
  }

  function closeTip() {
    setTip(null);
  }

  return (
    <li
      ref={rowRef}
      onMouseEnter={openTip}
      onMouseLeave={closeTip}
      onFocus={openTip}
      onBlur={closeTip}
      tabIndex={0}
      style={{
        display: "flex",
        gap: 8,
        padding: "8px 0",
        alignItems: "center",
        cursor: "default",
        outline: "none",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <span
        style={{
          color: "#a5b4fc",
          fontWeight: 800,
          fontSize: 14,
          lineHeight: "20px",
          flex: "0 0 14px",
        }}
      >
        ✓
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        {feature.title}
      </span>

      {tip
        ? createPortal(
            <FeatureTooltip
              desc={feature.desc}
              left={tip.left}
              top={tip.top}
            />,
            document.body
          )
        : null}
    </li>
  );
}

function FeatureTooltip({
  desc,
  left,
  top,
}: {
  desc: string;
  left: number;
  top: number;
}) {
  return (
    <div
      style={{
        position: "fixed",
        left,
        top,
        width: 280,
        zIndex: 200,
        background: "rgba(8,10,18,0.92)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 12,
        padding: "10px 12px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        color: "#fff",
        fontSize: 12,
        lineHeight: 1.5,
        pointerEvents: "none",
      }}
    >
      {desc}
    </div>
  );
}
